/**
 * juice.js — ONE feedback orchestrator for meaningful moments. Sits on top of
 * existing rewards (progress.js / progression.js) — never awards anything.
 *   juice("correct", { el })         → inline pop + soft cue (≤ 500 ms)
 *   juice("wrong",   { el })         → gentle nudge + soft cue
 *   juice("complete"|"badge"|"stamp"|"xp"|"levelup"|"discovery"|"story"|"surprise", detail)
 *      → mwq-juice event consumed by <JuiceLayer/> (non-blocking, skippable toast)
 * Respects mute (soundCues), prefers-reduced-motion and the age tier.
 */
import { playCue } from "@/lib/soundCues";
import { voiceLine } from "@/lib/companionVoice";
import { getUnifiedTier } from "@/lib/ageCopy";
import { track } from "@/lib/track";
import { trackCompletedActivity } from "@/lib/analytics";
import { maybeSurprise } from "@/lib/surprise";

const CUE = { correct: "win", wrong: "wrong", complete: "win", badge: "badge", stamp: "chest", xp: "tap", levelup: "badge", discovery: "chest", story: "reveal", surprise: "chest" };

export function reducedMotion() {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

export function currentTier() { return getUnifiedTier(); }

function replayClass(el, cls) {
  if (!el) return;
  el.classList.remove(cls);
  void el.offsetWidth;
  el.classList.add(cls);
  el.addEventListener("animationend", () => el.classList.remove(cls), { once: true });
}

const SPARK_COLORS = ["#FFC83D", "#35C9A5", "#3B72FF", "#FF765C"];
function sparks(el, n) {
  if (!el || reducedMotion() || n <= 0) return;
  const host = el.style.position ? el : (el.style.position = "relative", el);
  for (let i = 0; i < n; i++) {
    const s = document.createElement("span");
    const a = (i / n) * Math.PI * 2;
    s.className = "mwq-spark";
    s.style.background = SPARK_COLORS[i % SPARK_COLORS.length];
    s.style.left = "50%"; s.style.top = "50%";
    s.style.setProperty("--dx", `${Math.cos(a) * 42}px`);
    s.style.setProperty("--dy", `${Math.sin(a) * 30}px`);
    s.setAttribute("aria-hidden", "true");
    host.appendChild(s);
    s.addEventListener("animationend", () => s.remove(), { once: true });
  }
}

/** Floating "+N" label inside `el` (position:relative applied). */
export function floatLabel(el, text, color = "var(--color-reward-text)") {
  if (!el) return;
  if (!el.style.position) el.style.position = "relative";
  const f = document.createElement("span");
  f.className = "mwq-juice-float";
  f.textContent = text;
  f.style.cssText += `right:12px;top:-4px;color:${color};font-size:15px;`;
  f.setAttribute("aria-hidden", "true");
  el.appendChild(f);
  f.addEventListener("animationend", () => f.remove(), { once: true });
}

export function juice(kind, detail = {}) {
  const tier = detail.tier || currentTier();
  const cue = CUE[kind];
  if (cue && !(tier === "pioneers" && kind === "xp")) playCue(cue);

  if (kind === "correct" || kind === "wrong") {
    replayClass(detail.el, kind === "correct" ? "mwq-juice-correct" : "mwq-juice-wrong");
    if (kind === "correct") sparks(detail.el, tier === "seedlings" ? 8 : tier === "explorers" ? 6 : 0);
    return { text: "" };
  }

  const line = voiceLine(kind === "xp" ? "xp" : kind, { tier });
  try {
    window.dispatchEvent(new CustomEvent("mwq-juice", { detail: { kind, tier, line, ...detail, at: Date.now() } }));
  } catch { /* SSR */ }
  try { track("celebration_shown", { kind, tier, reduced: reducedMotion() ? 1 : 0 }); } catch { /* ignore */ }
  if (kind === "complete") {
    // Every meaningful completion goes through here → ONE analytics completion event (North-Star step 3).
    try { trackCompletedActivity({ activity_id: detail.id || detail.activity || detail.code || detail.type || "activity", strand: detail.subject || detail.type || "unknown", result: detail.result || "attempted" }); } catch { /* ignore */ }
    const sp = maybeSurprise(detail);
    if (sp) setTimeout(() => juice("surprise", { ...sp, tier }), 900);
  }
  return line;
}
